import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { parseISO, format } from "date-fns";
import { motion } from "framer-motion";
import { API_BASE_URL } from "../config/apiConfig";
import {
  FaBoxOpen,
  FaCalendarAlt,
  FaUser,
  FaMapMarkerAlt,
  FaMoneyBillWave,
} from "react-icons/fa";
import Navbar from "../components/Navbar";

const OrdersScreen = () => {
  const orders = useSelector((state) => state.orders.orders);
  const [statuses, setStatuses] = useState({});

  const parsePrice = (price) => {
    if (typeof price === "number") return price;
    if (typeof price === "string") {
      const n = parseFloat(price.replace(/[^\d.-]/g, ""));
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };

  // Fetch order statuses from adminhlc-api (MySQL) — Step 8 of the
  // Firebase->PHP migration. Requires id + email together (not id alone) —
  // sequential MySQL ids are guessable, unlike the old Firebase UUIDs, so
  // the endpoint refuses an id-only lookup. Email is already available
  // locally on each order's userDetails from checkout.
  useEffect(() => {
    if (!orders || orders.length === 0) return;
    let cancelled = false;

    orders.forEach((order) => {
      const email = order.userDetails?.email;
      if (!email) return;

      fetch(
        `${API_BASE_URL}/api/public/orders/status.php?id=${encodeURIComponent(order.id)}&email=${encodeURIComponent(email)}`
      )
        .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
        .then(({ ok, data }) => {
          if (cancelled) return;
          setStatuses((prev) => ({
            ...prev,
            [order.id]: ok ? (data.status || "Pending") : "Unavailable",
          }));
        })
        .catch((err) => {
          console.error("Failed to fetch order status:", err);
          if (!cancelled) {
            setStatuses((prev) => ({ ...prev, [order.id]: "Unavailable" }));
          }
        });
    });

    return () => {
      cancelled = true;
    };
  }, [orders]);

  return (
 <>
 <Navbar/>
    <div className="h-screen w-full bg-gradient-to-br from-purple-50 to-purple-100 flex flex-col items-center justify-start font-[Poppins] overflow-hidden mt-20">
      {/* 🔥 Header */}
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="text-3xl md:text-4xl font-extrabold mb-6 text-purple-800 flex items-center gap-3 mt-4"
      >
        <motion.span
          animate={{ rotate: [0, 10, -10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
        >
          <FaBoxOpen className="text-purple-600 drop-shadow-md" />
        </motion.span>
        Your Orders
      </motion.h2>

      {orders.length === 0 ? (
        <p className="text-gray-500 text-base mt-4">You haven’t placed any orders yet.</p>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full max-w-6xl overflow-y-auto px-4 pb-6"
          style={{ maxHeight: "calc(100vh - 100px)" }}
        >
          {orders.map((order, index) => {
            const orderStatus = statuses[order.id] || "Loading...";
            let badgeColor =
              orderStatus === "Delivered"
                ? "bg-green-100 text-green-800"
                : orderStatus === "Accepted"
                ? "bg-blue-100 text-blue-800"
                : orderStatus === "Rejected"
                ? "bg-red-100 text-red-800"
                : orderStatus === "Unavailable"
                ? "bg-gray-100 text-gray-600"
                : "bg-yellow-100 text-yellow-800";

            return (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay: index * 0.05, type: "spring", stiffness: 100 }}
                className="bg-white rounded-2xl shadow-md border border-purple-100 p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                {/* 🧾 Order Header */}
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-bold text-gray-900">
                    #{order.id}
                  </h3>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${badgeColor}`}
                  >
                    {orderStatus}
                  </span>
                </div>

                <p className="text-gray-500 text-sm flex items-center gap-1 mb-3">
                  <FaCalendarAlt className="text-purple-600" />
                  {format(parseISO(order.orderDate), "MMM d, yyyy")}
                </p>

                {/* 📦 Items */}
                <div className="border-t pt-3 space-y-2 text-[15px] text-gray-700">
                  {order.items.map((item) => (
                    <div
                      key={item.id}
                      className="flex justify-between items-center text-sm"
                    >
                      <p className="font-medium">
                        {item.name}{" "}
                        <span className="text-gray-500">×{item.quantity}</span>
                      </p>
                      <p className="font-semibold text-gray-900">
                        ₨ {(parsePrice(item.price) * item.quantity).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>

                {/* 💰 Total */}
                <div className="border-t mt-4 pt-3 flex justify-between items-center text-base font-semibold text-purple-700">
                  <span>Total:</span>
                  <span>₨ {order.total.toLocaleString()}</span>
                </div>

                {/* 👤 User Info */}
                <div className="mt-3 text-sm text-gray-600 space-y-1">
                  <p className="flex items-center gap-1 font-medium">
                    <FaUser className="text-purple-600" />
                    {order.userDetails.fullName}
                  </p>
                  <p>{order.userDetails.email}</p>
                  <p className="flex items-center gap-1">
                    <FaMapMarkerAlt className="text-purple-600" />
                    {order.userDetails.address}, {order.userDetails.city}
                  </p>
                  <p>📞 {order.userDetails.phone}</p>
                  <p className="flex items-center gap-1">
                    <FaMoneyBillWave className="text-purple-600" />
                    {order.userDetails.paymentMethod}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </div>
 </>
  );
};

export default OrdersScreen;
